import { SettingsPermissions } from "@redotech/account-common/permissions";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { Navigate, useLocation } from "react-router-dom";
import { csatDeepLinkTarget } from "../../../../setting/customer-satisfaction/csat-deep-links";

const FaqRedirect = () => {
  return <Navigate replace to="/support-v2/faq/home" />;
};

/** CSAT moved to the support product; keep old deep links landing on the right sub-page. */
const CsatSettingsRedirect = () => {
  const { search } = useLocation();
  return <Navigate replace to={csatDeepLinkTarget(search)} />;
};

export const supportSettingsRoutes: PermissionedRouteObject[] = [
  {
    handle: {
      breadcrumb: "General",
      permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
    },
    path: "general",
    children: [
      {
        index: true,
        lazy: async () => {
          const { SupportGeneral } =
            await import("@redotech/redo-merchant-support-app/setting/general");
          return { element: <SupportGeneral /> };
        },
      },
      {
        handle: { breadcrumb: "Contact Form" },
        path: "contact-form",
        lazy: async () => {
          const { ContactFormSettingsNew } =
            await import("@redotech/redo-merchant-support-app/setting/general/contact-form-settings-new");
          return { Component: ContactFormSettingsNew };
        },
      },
    ],
  },
  {
    handle: {
      breadcrumb: "Channels",
      permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
    },
    path: "channels",
    children: [
      {
        path: "",
        lazy: async () => {
          const { ChannelsPage } = await import("../../../../setting/channels");
          return { Component: ChannelsPage };
        },
      },
      {
        handle: { breadcrumb: "Attentive" },
        path: "attentive-connect",
        async lazy() {
          const { Connect } =
            await import("@redotech/redo-merchant-support-app/attentive/connect");
          return { element: <Connect /> };
        },
      },
      {
        handle: { breadcrumb: "Email" },
        path: "email-connect",
        async lazy() {
          const { EmailSetup } =
            await import("../../../../setting/email-setup");
          return { element: <EmailSetup /> };
        },
      },
      {
        handle: { breadcrumb: "Facebook Dev Tools" },
        path: "facebook-dev/:integrationId",
        lazy: async () => {
          const { FacebookDevTools } =
            // redont-allow-dynamic-import: Preserve route-level code splitting, matching the existing Gmail and Outlook dev-tool routes.
            await import("@redotech/redo-merchant-support-app/setting/facebook-dev-tools");
          return { Component: FacebookDevTools };
        },
      },
      {
        handle: { breadcrumb: "Gmail Dev Tools" },
        path: "gmail-dev/:integrationId",
        lazy: async () => {
          const { GmailDevTools } =
            await import("@redotech/redo-merchant-support-app/setting/gmail-dev-tools");
          return { Component: GmailDevTools };
        },
      },
      {
        handle: { breadcrumb: "Gorgias" },
        path: "gorgias-connect",
        async lazy() {
          const { Connect } =
            await import("@redotech/redo-merchant-support-app/gorgias/connect");
          return { element: <Connect /> };
        },
      },
      {
        handle: { breadcrumb: "Instagram Dev Tools" },
        path: "instagram-dev/:integrationId",
        lazy: async () => {
          const { InstagramDevTools } =
            // redont-allow-dynamic-import: Preserve route-level code splitting, matching the existing Gmail and Outlook dev-tool routes.
            await import("@redotech/redo-merchant-support-app/setting/instagram-dev-tools");
          return { Component: InstagramDevTools };
        },
      },
      {
        handle: { breadcrumb: "Meta" },
        path: "meta-connect",
        async lazy() {
          const { Connect } =
            await import("@redotech/redo-merchant-support-app/meta/connect");
          return { element: <Connect /> };
        },
      },
      {
        handle: { breadcrumb: "Meta" },
        path: "meta-settings",
        async lazy() {
          const { MetaSettings } =
            await import("../../../../setting/channel/meta-settings");
          return { element: <MetaSettings /> };
        },
      },
      {
        handle: { breadcrumb: "Outlook Dev Tools" },
        path: "outlook-dev/:integrationId",
        lazy: async () => {
          const { OutlookDevTools } =
            await import("@redotech/redo-merchant-support-app/setting/outlook-dev-tools");
          return { Component: OutlookDevTools };
        },
      },
      {
        handle: { breadcrumb: "Phone" },
        path: "phone-setup",
        async lazy() {
          const { PhoneSetup } =
            await import("@redotech/redo-merchant-support-app/phone/setup");
          return { element: <PhoneSetup /> };
        },
      },
      {
        handle: { breadcrumb: "Postscript" },
        path: "postscript-connect",
        async lazy() {
          const { Connect } =
            await import("@redotech/redo-merchant-support-app/postscript/connect");
          return { element: <Connect /> };
        },
      },
      {
        handle: { breadcrumb: "Richpanel" },
        path: "richpanel-connect",
        async lazy() {
          const { Connect } =
            await import("@redotech/redo-merchant-support-app/richpanel/connect");
          return { element: <Connect /> };
        },
      },
      {
        path: "meta-callback",
        async lazy() {
          const { Callback } =
            await import("@redotech/redo-merchant-support-app/meta/meta-callback");
          return { element: <Callback /> };
        },
      },
      {
        handle: { breadcrumb: "Support Email" },
        path: "supportEmail",
        lazy: async () => {
          const { SupportEmailInfo } =
            await import("@redotech/redo-merchant-support-app/setting/email-info");
          return { Component: SupportEmailInfo };
        },
      },
      {
        handle: { breadcrumb: "Tiktok" },
        path: "tiktok-callback",
        async lazy() {
          const { Callback } =
            await import("@redotech/redo-merchant-support-app/tiktok-callback");
          return { element: <Callback /> };
        },
      },
      {
        handle: { breadcrumb: "TikTok Shop" },
        path: "tiktok-settings",
        async lazy() {
          const { TiktokSettings } =
            await import("../../../../setting/channel/tiktok-settings");
          return { element: <TiktokSettings /> };
        },
      },
      {
        handle: { breadcrumb: "Voice" },
        path: "voice-settings",
        async lazy() {
          const { VoiceAndSmsSettings } =
            await import("../../../../setting/channel/voice-and-sms/voice-and-sms-settings");
          return { element: <VoiceAndSmsSettings /> };
        },
      },
      {
        path: "attentive-callback",
        async lazy() {
          const { Callback } =
            await import("@redotech/redo-merchant-support-app/attentive-callback");
          return { element: <Callback /> };
        },
      },
      {
        path: "gmail-callback",
        async lazy() {
          const { Callback } =
            await import("@redotech/redo-merchant-support-app/gmail-callback");
          return { element: <Callback /> };
        },
      },
      {
        path: "outlook-callback",
        async lazy() {
          const { Callback } =
            await import("@redotech/redo-merchant-support-app/outlook-callback");
          return { element: <Callback /> };
        },
      },
    ],
  },
  {
    handle: {
      breadcrumb: "CSAT",
      permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
    },
    path: "customer-satisfaction",
    element: <CsatSettingsRedirect />,
  },
  {
    handle: {
      breadcrumb: "FAQ",
      permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
    },
    path: "faq",
    element: <FaqRedirect />,
  },
  {
    handle: {
      breadcrumb: "Macros",
      permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
    },
    path: "macros",
    lazy: async () => {
      const { SupportV2MacrosPage } =
        await import("@redotech/redo-merchant-support-app/v2/setting/macros/macros");
      return { Component: SupportV2MacrosPage };
    },
  },
  {
    handle: {
      breadcrumb: "Rules",
      permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
    },
    path: "rules",
    children: [
      {
        path: "",
        lazy: async () => {
          const { RulesListRouter } =
            await import("@redotech/redo-merchant-support-app/setting/rules/rules-router");
          return { element: <RulesListRouter /> };
        },
      },
      {
        handle: { breadcrumb: "Edit default rule" },
        path: "edit-default/:defaultRuleId",
        lazy: async () => {
          const { EditDefaultRulePage } =
            await import("@redotech/redo-merchant-support-app/setting/rules/edit-default-rule-page");
          return { element: <EditDefaultRulePage /> };
        },
      },
      {
        handle: { breadcrumb: "Edit rule" },
        path: "edit/:ruleId",
        lazy: async () => {
          const { CreateRuleRouter } =
            await import("@redotech/redo-merchant-support-app/setting/rules/rules-router");
          return { element: <CreateRuleRouter /> };
        },
      },
      {
        handle: { breadcrumb: "Edit rule" },
        path: "edit-v2/:ruleId",
        lazy: async () => {
          const { CreateRuleRouter } =
            await import("@redotech/redo-merchant-support-app/setting/rules/rules-router");
          return { element: <CreateRuleRouter /> };
        },
      },
      {
        handle: { breadcrumb: "Edit rule" },
        path: "edit-v2/",
        lazy: async () => {
          const { CreateRuleRouter } =
            await import("@redotech/redo-merchant-support-app/setting/rules/rules-router");
          return { element: <CreateRuleRouter /> };
        },
      },
      {
        handle: { breadcrumb: "Edit rule" },
        path: "edit-v3/:ruleId",
        lazy: async () => {
          const { CreateRuleRouter } =
            await import("@redotech/redo-merchant-support-app/setting/rules/rules-router");
          return { element: <CreateRuleRouter /> };
        },
      },
      {
        handle: { breadcrumb: "Edit rule" },
        path: "edit-v3/",
        lazy: async () => {
          const { CreateRuleRouter } =
            await import("@redotech/redo-merchant-support-app/setting/rules/rules-router");
          return { element: <CreateRuleRouter /> };
        },
      },
      {
        handle: { breadcrumb: "New rule" },
        path: "new",
        lazy: async () => {
          const { CreateRuleRouter } =
            await import("@redotech/redo-merchant-support-app/setting/rules/rules-router");
          return { element: <CreateRuleRouter /> };
        },
      },
    ],
  },
  {
    handle: {
      breadcrumb: "Tags",
      permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
    },
    path: "tags",
    lazy: async () => {
      const { Tags } =
        await import("../../../../setting/conversation-tags/tags");
      return { Component: Tags };
    },
  },
  {
    handle: {
      breadcrumb: "Topics",
      permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
    },
    path: "topics",
    lazy: async () => {
      const { SupportV2TopicsPage } =
        await import("@redotech/redo-merchant-support-app/v2/setting/topics/topics");
      return { Component: SupportV2TopicsPage };
    },
  },
];
